import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useLockConfig, checkLocked } from '../hooks/useLockConfig';
import {
  Typography, Tag, Space, Spin, Button, Divider,
  Card, message, Modal, Input, Form, Select, DatePicker,
  Descriptions, Avatar, Dropdown, Table, List, Collapse, Checkbox, Popconfirm,
} from 'antd';
import dayjs from 'dayjs';
import {
  SaveOutlined, InfoCircleOutlined,
  WarningOutlined, BookOutlined, CommentOutlined, UserOutlined, LinkOutlined,
  PrinterOutlined, EditOutlined, DeleteOutlined, LikeOutlined, LikeFilled,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../App';
import { ProjectFyi } from '../components/ProjectParts';
import { formatTS } from '../utils/time';
import CoachingCreateModal from '../components/CoachingCreateModal';
import CommentBox, { CommentAttachments } from '../components/CommentBox';
import LinkifiedText from '../components/LinkifiedText';
import { PaperClipOutlined } from '@ant-design/icons';
import { useBackFallback, usePageLabel } from '../components/BackChip';
import UserAvatar from '../components/UserAvatar';

const STATUS_COLOR = {
  assigned:          'blue',
  in_progress:       'orange',
  completed:         'green',
  rescore_requested: 'purple',
  canceled:          'default',
};
const STATUS_LABEL = {
  assigned:          'Draft',
  in_progress:       'In Progress',
  completed:         'Completed',
  rescore_requested: 'Disputed',
  canceled:          'Canceled',
};
const SESSION_STATUS_COLOR = { draft: 'default', active: 'blue', completed: 'green', canceled: 'red' };
const SESSION_STATUS_LABEL = { draft: 'Draft', active: 'Active', completed: 'Completed', canceled: 'Canceled' };

function canManageCoaching(user) {
  return (user?.CanEditCoaching ?? 0) >= 2;
}

export default function ErrorSubmissionPage() {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const currentUser = useContext(UserContext);
  // The Back chip (Layout): back where you came from, else this list.
  const staffErr = (currentUser?.CanEditErrors ?? 0) >= 1;
  useBackFallback(staffErr ? '/errors' : '/my-errors', staffErr ? 'Error Search' : 'My Errors');
  usePageLabel(`Error #${id}`);
  const isAgent     = currentUser?.Role === 'agent';
  const isAdmin     = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin';

  const [sub,          setSub]          = useState(null);
  const [form,         setForm]         = useState(null);
  const [coaching,     setCoaching]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [answers,      setAnswers]      = useState({});
  const [pendingStatus,  setPendingStatus]  = useState(null);
  const [assignUsers,    setAssignUsers]    = useState([]);
  const [pendingAssign,  setPendingAssign]  = useState(null);
  const [notes,          setNotes]          = useState({});
  const [praised,        setPraised]        = useState({});
  const [comments,       setComments]       = useState([]);
  const [addingComment,  setAddingComment]  = useState(false);
  const [editComment,    setEditComment]    = useState({ id: null, text: '' });
  const [newComment,     setNewComment]     = useState('');

  // dispute modal
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeNote, setDisputeNote] = useState('');
  const [disputing,   setDisputing]   = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [canceling,     setCanceling]     = useState(false);

  // error question history
  const [qStatsRows,      setQStatsRows]      = useState([]);
  const [qStatsLoading,   setQStatsLoading]   = useState(false);
  const [flatSectionRows, setFlatSectionRows] = useState([]);
  const [flatLoading,     setFlatLoading]     = useState(false);

  const [externalRefDraft,  setExternalRefDraft]  = useState('');
  const [dateOfErrorDraft,  setDateOfErrorDraft]  = useState(null);
  const [sourceDraft,       setSourceDraft]       = useState(null);

  // completion modal (coaching prompt)
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);

  // coaching modal
  const [coachModalOpen,  setCoachModalOpen]  = useState(false);

  // print options
  const [printOptOpen,   setPrintOptOpen]   = useState(false);
  const [printComments,  setPrintComments]  = useState(false);
  const [printCoaching,  setPrintCoaching]  = useState(false);

  // sub-dependent derived values — must be after useState(sub) to avoid TDZ in production
  const isSubAgent   = sub != null && currentUser?.ID === sub?.AgentUserID;
  const isAssignedTo = sub != null && sub?.AssignedToUserID === currentUser?.ID;
  const isManager    = (currentUser?.CanEditErrors ?? 0) >= 3;
  const canEdit      = (currentUser?.CanEditErrors ?? 0) >= 2 && !isAgent && !isSubAgent && (isManager || isAssignedTo);

  const lockConfig     = useLockConfig();
  const isErrorLocked  = checkLocked(lockConfig, 'error', sub?.DateOfError);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/errors/${id}`)
      .then((r) => {
        const { submission, form: f, coaching: c } = r.data;
        setSub(submission);
        setForm(f);
        setCoaching(c || []);
        setPendingStatus(submission.Status);
        setPendingAssign(submission.AssignedToUserID ?? null);
        setExternalRefDraft(submission.ExternalRef || '');
        setDateOfErrorDraft(submission.DateOfError ? dayjs(submission.DateOfError) : null);
        setSourceDraft(submission.Source || null);
        const map = {};
        const noteMap = {};
        const praisedMap = {};
        (f?.Sections || []).forEach((sec) => {
          sec.Questions.forEach((q) => {
            if (q.QuestionType === 'error_count') {
              map[q.ID]        = q.FeedbackOnly ? 'feedback' : (q.ErrorCount ?? 0);
              noteMap[q.ID]    = q.TextAnswer ?? '';
              praisedMap[q.ID] = !!q.IsPraised;
            } else if (q.QuestionType === 'date') {
              map[q.ID] = q.TextAnswer ? dayjs(q.TextAnswer) : null;
            } else {
              map[q.ID] = q.TextAnswer ?? '';
            }
          });
        });
        setAnswers(map);
        setNotes(noteMap);
        setPraised(praisedMap);
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 403) {
          message.error('You do not have access to this error submission.');
          navigate(-1);
        } else if (status === 404) {
          message.error('Error submission not found.');
          navigate(-1);
        } else {
          message.error('Failed to load error submission.');
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  const loadComments = useCallback(() => {
    api.get(`/api/errors/${id}/comments`)
      .then((r) => setComments(r.data))
      .catch(() => {});
  }, [id]);

  useEffect(() => { load(); loadComments(); }, [load, loadComments]);

  useEffect(() => {
    if (!sub?.AgentUserID) return;
    const endDate   = dayjs();
    const startDate = dayjs(sub.DateOfError || sub.CreateDate).subtract(3, 'month').startOf('month');
    const isCompleted = sub?.Status === 'completed';
    const base = {
      agent_user_id: sub.AgentUserID,
      start_date:    startDate.format('YYYY-MM-DD'),
      end_date:      endDate.format('YYYY-MM-DD'),
      ...(isCompleted ? {} : { exclude_id: id }),
    };
    setQStatsLoading(true);
    api.get('/api/errors/question-stats', { params: { ...base, group_by: 'month' } })
      .then((r) => setQStatsRows(r.data))
      .catch(() => {})
      .finally(() => setQStatsLoading(false));
    setFlatLoading(true);
    api.get('/api/errors/question-stats', { params: { ...base, group_by: 'section_date' } })
      .then((r) => setFlatSectionRows(r.data))
      .catch(() => {})
      .finally(() => setFlatLoading(false));
  }, [sub, id]);

  useEffect(() => {
    if (!canEdit || !sub) return;
    const params = { can_edit_errors: 1 };
    if (sub.CampaignID)         params.campaign_id    = sub.CampaignID;
    if (sub.AssignedToUserID)   params.include_user_id = sub.AssignedToUserID;
    api.get('/api/users', { params })
      .then((r) => setAssignUsers(r.data))
      .catch(() => {});
  }, [canEdit, sub]);

  const isEditable = sub && ['assigned', 'in_progress', 'rescore_requested'].includes(sub.Status) && canEdit;

  const assignOptions = (() => {
    const base = assignUsers.map((u) => ({ label: u.DisplayName || u.Email, value: u.ID }));
    if (sub?.AssignedToUserID && !base.some((u) => u.value === sub.AssignedToUserID)) {
      base.unshift({ value: sub.AssignedToUserID, label: sub.AssignedToName || 'Unknown' });
    }
    return base;
  })();

  const buildPayload = useCallback(() => {
    const allQuestions = (form?.Sections || []).flatMap((s) => s.Questions || []);
    return Object.entries(answers).map(([qid, val]) => {
      const q = allQuestions.find((q) => q.ID === Number(qid));
      if (q?.QuestionType === 'error_count') {
        const isFeedback = val === 'feedback';
        return {
          question_id:   Number(qid),
          error_count:   isFeedback ? 0 : (val ?? 0),
          feedback_only: isFeedback ? 1 : 0,
          text_answer:   notes[Number(qid)]?.trim() || null,
          is_praised:    praised[Number(qid)] ? 1 : 0,
        };
      }
      const textVal = q?.QuestionType === 'date'
        ? (val ? val.format('YYYY-MM-DD') : null)
        : (val || null);
      return { question_id: Number(qid), error_count: 0, text_answer: textVal };
    });
  }, [answers, notes, praised, form]);

  const handleSaveAll = async (confirmed = false) => {
    if (!confirmed && pendingStatus === 'canceled' && pendingStatus !== sub?.Status) {
      Modal.confirm({
        title: 'Are you sure you want to change the Status to Canceled?',
        content: 'Canceling cannot be undone.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => handleSaveAll(true),
      });
      return;
    }
    if (isEditable && !sourceDraft) {
      message.error('Source is required before saving.');
      return;
    }
    const isCompletingNow = pendingStatus === 'completed' && sub?.Status !== 'completed';
    if (isEditable && isCompletingNow) {
      const missing = [];
      for (const sec of form?.Sections ?? []) {
        for (const q of sec.Questions ?? []) {
          if (!q.IsMandatory || q.QuestionType === 'error_count') continue;
          const val = answers[q.ID];
          const answered = q.QuestionType === 'info_select'
            ? !!val
            : (typeof val === 'string' ? val.trim() : !!val);
          if (!answered) missing.push(`"${q.QuestionText}" (${sec.Name})`);
        }
      }
      if (missing.length > 0) {
        message.error(`Cannot complete — mandatory questions unanswered: ${missing.join(', ')}`);
        return;
      }
    }
    setSaving(true);
    try {
      const assignChanged  = pendingAssign !== (sub?.AssignedToUserID ?? null);
      const statusChanged  = pendingStatus && pendingStatus !== sub?.Status;
      const metaThunks = [];
      if (externalRefDraft.trim() !== (sub?.ExternalRef || '')) {
        metaThunks.push(() => api.patch(`/api/errors/${id}/external-ref`, { external_ref: externalRefDraft.trim() || null }));
      }
      const dateOfErrorStr = dateOfErrorDraft ? dateOfErrorDraft.format('YYYY-MM-DD') : null;
      if (dateOfErrorStr !== (sub?.DateOfError || null)) {
        metaThunks.push(() => api.patch(`/api/errors/${id}/date-of-error`, { date_of_error: dateOfErrorStr }));
      }
      if (sourceDraft !== (sub?.Source || null)) {
        metaThunks.push(() => api.patch(`/api/errors/${id}/source`, { source: sourceDraft }));
      }
      if (!isEditable && !metaThunks.length && !assignChanged && !statusChanged) { message.info('No changes to save.'); setSaving(false); return; }
      // 1. Save answers + comment
      if (isEditable) {
        await api.put(`/api/errors/${id}/answers`, { answers: buildPayload() });
        if (newComment.trim()) {
          await api.post(`/api/errors/${id}/comments`, { comment: newComment.trim() });
          setNewComment('');
          loadComments();
        }
      }
      // 2. Save other metadata in parallel
      if (metaThunks.length) await Promise.all(metaThunks.map((fn) => fn()));
      // 3. Status
      if (statusChanged) await api.put(`/api/errors/${id}/status`, { status: pendingStatus });
      // 4. Assignment
      if (assignChanged) await api.put(`/api/errors/${id}/assign`, { assigned_to_user_id: pendingAssign || null });
      message.success('Saved.');
      if (isCompletingNow) {
        load();
        setFollowUpModalOpen(true);
      } else {
        load();
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async (text, files) => {
    setAddingComment(true);
    try {
      const res = await api.post(`/api/errors/${id}/comments`, { comment: text });
      const commentId = res.data.id;
      for (const f of files) {
        const fd = new FormData();
        fd.append('comment_type', 'error');
        fd.append('comment_id', String(commentId));
        fd.append('file', f.file, f.name);
        await api.post('/api/attachments', fd);
      }
      loadComments();
    } catch {
      message.error('Failed to add comment.');
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/api/errors/${id}/comments/${commentId}`);
      loadComments();
    } catch {
      message.error('Failed to delete comment.');
    }
  };

  const handleSaveComment = async () => {
    const text = editComment.text.trim();
    if (!text) return;
    try {
      await api.put(`/api/errors/${id}/comments/${editComment.id}`, { comment: text });
      setEditComment({ id: null, text: '' });
      loadComments();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Failed to update comment.');
    }
  };

  const handleDispute = async () => {
    if (!disputeNote.trim()) { message.warning('Please enter a reason.'); return; }
    setDisputing(true);
    try {
      await api.put(`/api/errors/${id}/status`, { status: 'rescore_requested', rescore_note: disputeNote.trim() });
      message.success('Submission disputed.');
      setDisputeOpen(false);
      setDisputeNote('');
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to dispute.');
    } finally {
      setDisputing(false);
    }
  };


  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      await api.post(`/api/errors/${id}/acknowledge`);
      message.success('Acknowledged.');
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to acknowledge.');
    } finally {
      setAcknowledging(false);
    }
  };

  const handleCancelSubmission = async () => {
    setCanceling(true);
    try {
      await api.put(`/api/errors/${id}/status`, { status: 'canceled' });
      message.success('Status set to Canceled.');
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to cancel.');
    } finally {
      setCanceling(false);
    }
  };

  // Print is deferred to the modal's afterClose rather than a timer.
  //
  // window.print() blocks the JS thread until the dialog is dismissed. Firing it
  // 150ms after closing the modal could land while Ant was still unmounting, so
  // the body scroll lock and the full-screen mask it applies never got cleaned
  // up -- the page was left unscrollable with an invisible overlay swallowing
  // clicks, which reads as the whole site freezing. Whether it happened came
  // down to machine speed and how long the dialog was held open, which is why
  // it reproduced for some people and not others.
  //
  // These MUST stay above the loading/not-found early returns below. Hooks
  // after a conditional return do not run on every render, so React sees a
  // different hook count once loading finishes and throws, blanking the page.
  const printTitleRef   = useRef(null);
  const pendingPrintRef = useRef(false);

  // Restored on afterprint, not after window.print() returns: some browsers
  // return immediately and print asynchronously, so restoring synchronously
  // could rename the file mid-save.
  useEffect(() => {
    const restore = () => {
      if (printTitleRef.current != null) {
        document.title = printTitleRef.current;
        printTitleRef.current = null;
      }
    };
    window.addEventListener('afterprint', restore);
    return () => {
      window.removeEventListener('afterprint', restore);
      restore();
    };
  }, []);

  // Spinner on the first load only: reloads after a save keep the page, so a
  // screenshot attached to an unsent comment is not thrown away.
  if (loading && !sub) return <Spin style={{ display: 'block', margin: '80px auto' }} />;
  if (!sub)    return <Typography.Text type="secondary">Submission not found.</Typography.Text>;

  const totalErrors = (() => {
    const allQuestions = (form?.Sections || []).flatMap((s) => s.Questions || []);
    return allQuestions
      .filter((q) => q.QuestionType === 'error_count')
      .reduce((s, q) => s + (answers[q.ID] === 1 ? 1 : 0), 0);
  })();

  const totalFeedbacks = (() => {
    const allQuestions = (form?.Sections || []).flatMap((s) => s.Questions || []);
    return allQuestions
      .filter((q) => q.QuestionType === 'error_count')
      .reduce((s, q) => s + (answers[q.ID] === 'feedback' ? 1 : 0), 0);
  })();

  const qHistoryRange = sub ? (() => {
    const end   = dayjs();
    const start = dayjs(sub.DateOfError || sub.CreateDate).subtract(3, 'month').startOf('month');
    return { start, end };
  })() : null;

  const isAcknowledged = !!sub.AcknowledgedAt;

  const handlePrint = () => setPrintOptOpen(true);
  const doPrint = () => {
    const slug  = (str) => (str || '').replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    const date  = sub.DateOfError || dayjs().format('YYYY-MM-DD');
    const title = `${date}_${sub.ID}_Error_${slug(sub.Campaign_Name)}_${slug(sub.AgentName)}`;
    printTitleRef.current = document.title;
    document.title = title;
    pendingPrintRef.current = true;
    setPrintOptOpen(false);
  };

  const printAfterModalClosed = () => {
    if (!pendingPrintRef.current) return;   // a plain cancel must not print
    pendingPrintRef.current = false;
    window.print();
  };

  const disputeDeadline = (() => {
    const completedStr = sub.CompletedAtStr || sub.CompletedAt;
    if (!completedStr) return null;
    return dayjs(completedStr).add(sub.DisputeWindowDays ?? 4, 'day');
  })();

  const disputeWindowClosed = (() => {
    if (isAdmin || isAssignedTo) return false;
    const base         = sub.DisputeWindowDays ?? 4;
    const completedStr = sub.CompletedAtStr || sub.CompletedAt;
    if (base === 0) return true;
    const deadline = completedStr ? dayjs(completedStr).add(base, 'day') : null;
    return deadline ? dayjs().isAfter(deadline) : false;
  })();

  const isErrorAgent = isAgent || isSubAgent;
  const isViewOnlyErrors = (currentUser?.CanEditErrors ?? 0) === 1 && currentUser?.ID !== sub?.AgentUserID;
  const agentCanComment = !isViewOnlyErrors && (
    !isErrorAgent
    || sub.Status === 'rescore_requested'
    || (sub.Status === 'completed' && !disputeWindowClosed && !isAcknowledged)
  );

  const qStatsMonths = [...new Set(qStatsRows.map((r) => r.MonthKey))].sort();
  const qStatsMLabels = {};
  qStatsMonths.forEach((mk) => { qStatsMLabels[mk] = dayjs(mk + '-01').format('MMM YYYY'); });

  const qStatsByKey = {};
  const qStatsKeyOrder = [];
  const qSectionOrder = {};
  qStatsRows.forEach((r) => {
    const k = `${r.SectionName}||${r.QuestionText}`;
    if (!qStatsByKey[k]) {
      const idx = qStatsKeyOrder.length;
      qStatsByKey[k] = { _key: k, _idx: idx, SectionName: r.SectionName, QuestionText: r.QuestionText };
      qStatsKeyOrder.push(k);
      if (!(r.SectionName in qSectionOrder)) qSectionOrder[r.SectionName] = idx;
    }
    qStatsByKey[k][r.MonthKey] = { errors: r.TotalErrors || 0, feedbacks: r.TotalFeedbacks || 0 };
  });
  // Sort so sections are consecutive even if API returned them interleaved
  const qStatsPivotData = qStatsKeyOrder
    .map((k) => qStatsByKey[k])
    .sort((a, b) => {
      const sA = qSectionOrder[a.SectionName];
      const sB = qSectionOrder[b.SectionName];
      if (sA !== sB) return sA - sB;
      return a._idx - b._idx;
    });

  const qSecByKey = {};
  const qSecKeyOrder = [];
  qStatsRows.forEach((r) => {
    if (!qSecByKey[r.SectionName]) {
      qSecByKey[r.SectionName] = { _key: r.SectionName, SectionName: r.SectionName };
      qSecKeyOrder.push(r.SectionName);
    }
    const prevSec = qSecByKey[r.SectionName][r.MonthKey] || { errors: 0, feedbacks: 0 };
    qSecByKey[r.SectionName][r.MonthKey] = { errors: prevSec.errors + (r.TotalErrors || 0), feedbacks: prevSec.feedbacks + (r.TotalFeedbacks || 0) };
  });
  const qSecPivotData = qSecKeyOrder.map((k) => qSecByKey[k]);

  const qSecCols = [
    { title: 'Section', dataIndex: 'SectionName', key: '__sec', ellipsis: true },
    ...qStatsMonths.map((mk) => ({
      title: qStatsMLabels[mk] || mk, key: mk, width: 120, align: 'center',
      render: (_, r) => {
        const cell = r[mk];
        if (!cell || (cell.errors === 0 && cell.feedbacks === 0)) return <span style={{ color: '#d9d9d9' }}>—</span>;
        return (
          <div>
            {cell.errors > 0 && <Tag color="red" style={{ margin: 0 }}>{cell.errors} {cell.errors === 1 ? 'error' : 'errors'}</Tag>}
            {cell.feedbacks > 0 && <Tag color="orange" style={{ margin: '2px 0 0' }}>{cell.feedbacks} {cell.feedbacks === 1 ? 'feedback' : 'feedbacks'}</Tag>}
          </div>
        );
      },
    })),
  ];

  const qStatsCols = [
    {
      title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 150, ellipsis: true,
      onCell: (r, i) => {
        const prev = i > 0 ? qStatsPivotData[i - 1].SectionName : null;
        if (r.SectionName === prev) return { rowSpan: 0 };
        let span = 1;
        while (i + span < qStatsPivotData.length && qStatsPivotData[i + span].SectionName === r.SectionName) span++;
        return { rowSpan: span };
      },
    },
    { title: 'Question', dataIndex: 'QuestionText', key: '__q', ellipsis: true },
    ...qStatsMonths.map((mk) => ({
      title: qStatsMLabels[mk] || mk, key: mk, width: 120, align: 'center',
      render: (_, r) => {
        const cell = r[mk];
        if (!cell || (cell.errors === 0 && cell.feedbacks === 0)) return <span style={{ color: '#d9d9d9' }}>—</span>;
        return (
          <div>
            {cell.errors > 0 && <Tag color="red" style={{ margin: 0 }}>{cell.errors} {cell.errors === 1 ? 'error' : 'errors'}</Tag>}
            {cell.feedbacks > 0 && <Tag color="orange" style={{ margin: '2px 0 0' }}>{cell.feedbacks} {cell.feedbacks === 1 ? 'feedback' : 'feedbacks'}</Tag>}
          </div>
        );
      },
    })),
  ];

  return (
    <>
      <style>{`
        @media print {
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          .ant-layout { min-height: 0 !important; background: white !important; }
          .ant-layout-header { display: none !important; }
          .ant-layout-content { padding: 0 !important; margin: 0 !important; background: white !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .print-only.ant-tag { display: inline-flex !important; }
          .ant-space-vertical { gap: 12px !important; }
          .ant-collapse-content-hidden { display: block !important; opacity: 1 !important; }
          .ant-collapse-content { height: auto !important; overflow: visible !important; }
          .ant-collapse-expand-icon { display: none !important; }
          .ant-card { break-inside: avoid; page-break-inside: avoid; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Print-only header */}
      <div className="print-only" style={{ marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #000' }}>
        <div><strong>Error Submission #{sub.ID} &mdash; Status: {STATUS_LABEL[sub.Status] ?? sub.Status}</strong></div>
        <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>Printed: {dayjs().format('YYYY-MM-DD HH:mm')}</div>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {(currentUser?.CanEditErrors ?? 0) >= 1 && (
          <Button className="no-print" icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </Button>
        )}
        <Typography.Title level={4} style={{ margin: 0 }}>
          Error Submission #{sub.ID}
          {sub.ContactID && (
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: 'nav',    label: 'Navigate to contact', icon: <LinkOutlined />, onClick: () => navigate(`/calls/${sub.ContactID}`) },
                  { key: 'window', label: 'Open in new window',  icon: <LinkOutlined />, onClick: () => window.open(`/calls/${sub.ContactID}`, '_blank') },
                ],
              }}
            >
              <Typography.Link style={{ fontSize: 'inherit', fontWeight: 'inherit', marginLeft: 8 }}>
                — Contact {sub.ContactID}
              </Typography.Link>
            </Dropdown>
          )}
        </Typography.Title>
        <Tag color={STATUS_COLOR[sub.Status] ?? 'default'} style={{ fontSize: 13 }}>
          Status: {STATUS_LABEL[sub.Status] ?? sub.Status}
        </Tag>
        {totalErrors > 0 && <Tag color="red">{totalErrors} total error(s) on this submission</Tag>}
        {totalFeedbacks > 0 && <Tag color="orange">{totalFeedbacks} feedback(s) on this submission</Tag>}
        {sub.CreateDate && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Created: {sub.CreateDate}
          </Typography.Text>
        )}
        {sub.CompletedAt && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Completed: {formatTS(sub.CompletedAt)}
          </Typography.Text>
        )}
        {(sub.CompletedAtStr || sub.CompletedAt) && sub.DisputeWindowDays > 0 && !disputeWindowClosed && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Acknowledge by: {dayjs(sub.CompletedAtStr || sub.CompletedAt).add(sub.DisputeWindowDays ?? 4, 'day').format('YYYY-MM-DD')}
          </Typography.Text>
        )}
      </div>

      {/* Sticky QA action bar — status, assigned to, save */}
      {isEditable && (
        <div className="no-print" style={{
          position: 'sticky', top: 0, zIndex: 20,
          background: 'var(--ant-color-bg-layout)',
          borderBottom: '1px solid var(--ant-color-border)',
          padding: '8px 0', marginBottom: 16,
        }}>
          <Space wrap align="center">
            {!['completed', 'canceled'].includes(sub.Status) && (
              <Space size={4} align="center">
                <Typography.Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Status:</Typography.Text>
                <Select
                  value={pendingStatus}
                  onChange={setPendingStatus}
                  options={[
                    { label: 'Draft',       value: 'assigned'  },
                    { label: 'Completed',   value: 'completed' },
                    { label: 'Canceled',    value: 'canceled'  },
                    ...(sub.Status === 'rescore_requested' ? [{ label: 'Disputed', value: 'rescore_requested' }] : []),
                  ]}
                  style={{ width: 160 }}
                />
              </Space>
            )}
            <Space size={4} align="center">
              <Typography.Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Assigned To:</Typography.Text>
              <Select
                value={pendingAssign ?? undefined}
                onChange={(v) => setPendingAssign(v ?? null)}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Unassigned"
                options={assignOptions}
                style={{ width: 200 }}
                disabled={['completed', 'canceled'].includes(sub.Status)}
              />
            </Space>
            <Button type="primary" icon={<SaveOutlined />} onClick={() => handleSaveAll()} loading={saving}>
              Save
            </Button>
          </Space>
        </div>
      )}

      {/* Sticky Acknowledge + Dispute bar — stacks below QA bar when both visible */}
      {sub.Status === 'completed' && (currentUser?.ID === sub?.AgentUserID || currentUser?.ID === sub?.AssignedToUserID || currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin') && (
        <div className="no-print" style={{
          position: 'sticky', top: isEditable ? 41 : 0, zIndex: 19,
          background: 'var(--ant-color-bg-layout)',
          borderBottom: '1px solid var(--ant-color-border)',
          padding: '8px 0', marginBottom: 16,
        }}>
          <Space size={8} align="center">
            {!isAcknowledged && (isSubAgent || isAssignedTo || canEdit)
              ? <Button size="small" type="primary" loading={acknowledging} onClick={handleAcknowledge}>Acknowledge</Button>
              : isAcknowledged ? <Typography.Text type="secondary" style={{ fontSize: 13 }}>Acknowledged on: {sub.AcknowledgedAt}</Typography.Text> : null}
            {!isAcknowledged && (isSubAgent || canEdit) && !isErrorLocked && !sub.RescoreNote && !((sub.DisableDispute || sub.DisputeWindowDays === 0) && !isAdmin && !isAssignedTo) && (
              disputeWindowClosed
                ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>The dispute deadline has passed.</Typography.Text>
                : <Button size="small" danger icon={<WarningOutlined />} onClick={() => setDisputeOpen(true)}>Dispute</Button>
            )}
            {(isAdmin || (isAssignedTo && canEdit)) && !isErrorLocked && (
              <Popconfirm
                title="Are you sure you want to change the Status to Canceled?"
                description="Canceling cannot be undone."
                okText="Yes"
                cancelText="No"
                onConfirm={handleCancelSubmission}
              >
                <Button size="small" danger loading={canceling}>Set to Canceled</Button>
              </Popconfirm>
            )}
          </Space>
        </div>
      )}


      <Space direction="vertical" style={{ width: '100%' }} size="large">

      {/* Info */}
      <Descriptions
        bordered size="small" column={2}
        labelStyle={{ width: 120, whiteSpace: 'nowrap' }}
        contentStyle={{ wordBreak: 'break-word' }}
      >
        {/* span=2 because Agent No used to sit beside this. Dropping that item
            without widening this one would re-pair every row below, splitting
            Date of Error from Ack. Deadline. */}
        <Descriptions.Item label="Agent" span={2}>
          {sub.AgentName
            ? (
              (currentUser?.CanViewAgentSummary ?? 0) >= 1
                ? <Typography.Link onClick={() => navigate(
                    // Errors are keyed on the QAUser, not on a dialer agent
                    // number. AgentUserID is always populated here and
                    // addresses the person directly; the name match is only a
                    // last resort.
                    sub.AgentUserID
                      ? `/agents?user_id=${encodeURIComponent(sub.AgentUserID)}`
                      : `/agents?agent_name=${encodeURIComponent(sub.AgentName)}`
                  )}>
                    {sub.AgentName}
                  </Typography.Link>
                : sub.AgentName
            )
            : sub.AgentUserID || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Campaign">{sub.Campaign_Name}</Descriptions.Item>
        <Descriptions.Item label="Form">{sub.FormName}</Descriptions.Item>
        <Descriptions.Item label="Assigned To">{sub.AssignedToName || '—'}</Descriptions.Item>
        <Descriptions.Item label="Submitted By">{sub.SubmittedByName}</Descriptions.Item>
        <Descriptions.Item label="Date of Error">
          {canEdit && !['completed', 'canceled'].includes(sub.Status) && !isErrorLocked ? (
            <DatePicker
              value={dateOfErrorDraft}
              onChange={(d) => setDateOfErrorDraft(d)}
              format="YYYY-MM-DD"
              style={{ width: 160 }}
              disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
            />
          ) : (sub.DateOfError || '—')}
        </Descriptions.Item>
        {disputeDeadline && (
          <Descriptions.Item label="Ack. Deadline">
            <Typography.Text type={dayjs().isAfter(disputeDeadline) ? 'secondary' : undefined}
              style={dayjs().isAfter(disputeDeadline) ? undefined : { color: '#fa8c16' }}>
              {disputeDeadline.format('YYYY-MM-DD')}
              {dayjs().isAfter(disputeDeadline) && ' (passed)'}
            </Typography.Text>
          </Descriptions.Item>
        )}
        <Descriptions.Item label={<span>Source <span style={{ color: '#ff4d4f' }}>*</span></span>}>
          {canEdit && !['completed', 'canceled'].includes(sub.Status) ? (
            <Select
              value={sourceDraft}
              onChange={(v) => setSourceDraft(v ?? null)}
              placeholder="Select source…"
              style={{ width: '100%' }}
              options={[
                { label: 'Ticket Review',  value: 'Ticket Review'  },
                { label: 'Support/Tier 2', value: 'Support/Tier 2' },
                { label: 'Client Email',   value: 'Client Email'   },
                { label: 'Call Review',    value: 'Call Review'    },
                { label: 'Form Response',  value: 'Form Response'  },
              ]}
            />
          ) : (sub.Source || '—')}
        </Descriptions.Item>
        <Descriptions.Item label="External Ref">
          {canEdit ? (
            <Input
              value={externalRefDraft}
              onChange={(e) => setExternalRefDraft(e.target.value)}
              placeholder="None"
              style={{ flex: 1, width: '100%' }}
              disabled={['completed', 'canceled'].includes(sub.Status)}
            />
          ) : (sub.ExternalRef || '—')}
        </Descriptions.Item>
        {sub.RescoreNote && (
          <Descriptions.Item label="Dispute Reason" span={2}><strong>{sub.RescoreNote}</strong></Descriptions.Item>
        )}
      </Descriptions>

      {/* Error Question History — collapsed by default */}
      {sub?.AgentName && (
        <div className="no-print"><Collapse
          defaultActiveKey={[]}
          items={[{
            key: 'history',
            label: (
              <span>
                Error Question History
                <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8, fontWeight: 400 }}>
                  click to expand / collapse
                </Typography.Text>
              </span>
            ),
            children: (
              <>
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                  Completed errors for {sub.AgentName}
                  {qHistoryRange && ` · ${qHistoryRange.start.format('MMM D, YYYY')} — ${qHistoryRange.end.format('MMM D, YYYY')}`}
                  {' '}{sub?.Status === 'completed' ? '(including this submission)' : '(excluding this submission)'}
                </Typography.Text>
                <Table
                  rowKey="_key"
                  dataSource={qSecPivotData}
                  columns={qSecCols}
                  loading={qStatsLoading}
                  size="small"
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  bordered
                  style={{ marginBottom: 16 }}
                  locale={{ emptyText: 'No completed error submissions in this date range.' }}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                  Section History by Submission
                </Typography.Text>
                <Table
                  rowKey={(r) => `${r.SubmissionID}||${r.SectionName}`}
                  dataSource={flatSectionRows}
                  loading={flatLoading}
                  size="small"
                  pagination={false}
                  bordered
                  scroll={{ x: 'max-content' }}
                  style={{ marginBottom: 16 }}
                  locale={{ emptyText: 'No completed error submissions in this date range.' }}
                  columns={[
                    { title: 'Submission ID', dataIndex: 'SubmissionID', key: 'SubmissionID', width: 120, align: 'right',
                      // window.open, not <a target="_blank">: Chrome clones sessionStorage
                      // into a script-opened window but not into one opened by an anchor.
                      // MSAL keeps the signed-in account there, so the anchor version
                      // landed on a fresh sign-in. Same approach as AgentLink on the DBAL
                      // page, which is why that one has always worked.
                      render: (v) => (
                        <Typography.Link onClick={() => window.open(`/errors/${v}`, '_blank')}>
                          {v}
                        </Typography.Link>
                      ) },
                    { title: 'Section',   dataIndex: 'SectionName', key: 'SectionName', width: 200, ellipsis: true },
                    { title: 'Date of Error', dataIndex: 'DateOfError', key: 'DateOfError', width: 120 },
                      { title: 'Created Date',  dataIndex: 'CreateDate',  key: 'CreateDate',  width: 120 },
                      {
                        // Coaching sessions raised off that submission, canceled ones
                        // excluded by the API. window.open for the same reason as the
                        // Submission ID link above.
                        title: 'Coachings', dataIndex: 'Coachings', key: 'Coachings', width: 260,
                        render: (list) => !list?.length
                          ? <Typography.Text type="secondary">-</Typography.Text>
                          : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {list.map((c) => (
                                <div key={c.ID} style={{ fontSize: 12, lineHeight: 1.4 }}>
                                  <Typography.Link onClick={() => window.open(`/coaching/sessions/${c.ID}`, '_blank')}>
                                    #{c.ID}
                                  </Typography.Link>
                                  {c.CreatedOn && <span style={{ color: '#8c8c8c' }}> · {c.CreatedOn}</span>}
                                  {c.RequestType && <span style={{ color: '#8c8c8c' }}> · {c.RequestType}</span>}
                                  {c.Linked != null && <span style={{ color: '#8c8c8c' }}> · {c.Linked ? 'Linked Sections' : 'All Sections'}</span>}
                                  <Tag
                                    color={SESSION_STATUS_COLOR[c.Status] ?? 'default'}
                                    style={{ marginLeft: 6 }}
                                  >
                                    {SESSION_STATUS_LABEL[c.Status] ?? c.Status}
                                  </Tag>
                                </div>
                              ))}
                            </div>
                          ),
                      },
                    { title: 'Errors',    dataIndex: 'TotalErrors',    key: 'TotalErrors',    width: 90,  align: 'center',
                      render: (v) => v > 0 ? <Tag color="red">{v}</Tag>    : <span style={{ color: '#d9d9d9' }}>—</span> },
                    { title: 'Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 100, align: 'center',
                      render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <span style={{ color: '#d9d9d9' }}>—</span> },
                  ]}
                />
                <Table
                  rowKey="_key"
                  dataSource={qStatsPivotData}
                  columns={qStatsCols}
                  loading={qStatsLoading}
                  size="small"
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  bordered
                  locale={{ emptyText: 'No completed error submissions in this date range.' }}
                />
              </>
            ),
          }]}
        /></div>
      )}

      {/* Project tasks about this agent that the viewer is on (hidden when none). */}
      <div style={{ marginTop: 16 }}>
        <ProjectFyi page="errors" agentUserId={sub?.AgentUserID} campaignId={sub?.CampaignID} currentUser={currentUser}
          recordType="error" recordId={sub?.ID} />
      </div>

      {/* Form sections */}
      {form && (
        <Space size={4}>
          <InfoCircleOutlined style={{ color: '#595959', fontSize: 12 }} />
          <Typography.Text style={{ fontSize: 12, color: '#595959' }}>
            {form.MaxOneErrorPerSection !== 0
              ? 'This form counts max 1 error per section'
              : 'This form counts all errors in a section'}
          </Typography.Text>
        </Space>
      )}
      {(() => {
        const isCompleted = sub.Status === 'completed';

        const renderQuestionList = (questions) => (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            {questions.map((q) => (
              <div key={q.ID} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0', borderBottom: '1px solid #cce0ff' }}>
                <div style={{ flex: 1, paddingTop: q.QuestionType === 'error_count' ? 4 : 0 }}>
                  {q.QuestionText}
                  {q.IsMandatory && q.QuestionType !== 'error_count' ? <Typography.Text type="danger" style={{ marginLeft: 3 }}>*</Typography.Text> : null}
                  {q.Guidance && (
                    <div style={{ fontSize: 12, color: '#595959', marginTop: 2 }}>{q.Guidance}</div>
                  )}
                </div>
                {q.QuestionType === 'error_count' && (
                  isEditable ? (
                    <Space direction="vertical" size={4} style={{ alignItems: 'flex-end', width: 700 }}>
                      <Space size={8}>
                        <Select
                          value={answers[q.ID] ?? 0}
                          onChange={(v) => setAnswers((prev) => ({ ...prev, [q.ID]: v }))}
                          style={{ width: 140 }}
                          options={[
                            { label: '—',             value: 0 },
                            { label: 'Feedback Only', value: 'feedback' },
                            { label: 'Error',         value: 1 },
                          ]}
                        />
                        <Button
                          className="no-print"
                          type={praised[q.ID] ? 'primary' : 'default'}
                          icon={praised[q.ID] ? <LikeFilled /> : <LikeOutlined />}
                          title="Praise good work"
                          onClick={() => setPraised((prev) => ({ ...prev, [q.ID]: !prev[q.ID] }))}
                        />
                        {praised[q.ID] && (
                          <Tag className="print-only" color="success" icon={<LikeFilled />}>Praised</Tag>
                        )}
                      </Space>
                      <Input.TextArea
                        placeholder="Notes (optional)…"
                        value={notes[q.ID] ?? ''}
                        onChange={(e) => setNotes((prev) => ({ ...prev, [q.ID]: e.target.value }))}
                        autoSize={{ minRows: 1, maxRows: 10 }}
                        maxLength={4000}
                        showCount
                        style={{ width: 700 }}
                      />
                    </Space>
                  ) : (
                    <div style={{ textAlign: 'right' }}>
                      {praised[q.ID] && <Tag color="success" icon={<LikeFilled />} style={{ marginBottom: 4 }}>Praised</Tag>}
                      {answers[q.ID] === 1          && <Tag color="red">Error</Tag>}
                      {answers[q.ID] === 'feedback'  && <Tag color="orange">Feedback Only</Tag>}
                      {notes[q.ID]?.trim() && (
                        <div style={{ fontSize: 13, color: '#595959', marginTop: 4, maxWidth: 420, textAlign: 'left', whiteSpace: 'pre-wrap' }}>
                          {notes[q.ID]}
                        </div>
                      )}
                    </div>
                  )
                )}
                {(q.QuestionType === 'text' || q.QuestionType === 'input') && (
                  isEditable ? (
                    <Input.TextArea
                      value={answers[q.ID] ?? ''}
                      onChange={(e) => setAnswers((prev) => ({ ...prev, [q.ID]: e.target.value }))}
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      style={{ width: 260 }}
                    />
                  ) : (
                    <Typography.Text style={{ maxWidth: 420, whiteSpace: 'pre-wrap' }}>{answers[q.ID] || '—'}</Typography.Text>
                  )
                )}
                {q.QuestionType === 'date' && (
                  isEditable ? (
                    <DatePicker
                      value={answers[q.ID] ?? null}
                      onChange={(d) => setAnswers((prev) => ({ ...prev, [q.ID]: d }))}
                      format="YYYY-MM-DD"
                      style={{ width: 160 }}
                    />
                  ) : (
                    <Typography.Text>{answers[q.ID] ? answers[q.ID].format('YYYY-MM-DD') : '—'}</Typography.Text>
                  )
                )}
                {q.QuestionType === 'info_select' && (
                  isEditable ? (
                    <Select
                      value={answers[q.ID] || undefined}
                      onChange={(v) => setAnswers((prev) => ({ ...prev, [q.ID]: v }))}
                      allowClear
                      placeholder="Select…"
                      style={{ width: 200 }}
                      options={(q.Options || []).map((o) => ({ label: o.OptionText, value: o.OptionText }))}
                    />
                  ) : (
                    <Typography.Text>{answers[q.ID] || '—'}</Typography.Text>
                  )
                )}
              </div>
            ))}
          </Space>
        );

        const secLabel = (sec) => {
          const ecQs         = sec.Questions.filter((q) => q.QuestionType === 'error_count');
          const rawErrors    = ecQs.filter((q) => answers[q.ID] === 1).length;
          const rawFeedbacks = ecQs.filter((q) => answers[q.ID] === 'feedback').length;
          const maxOne       = form?.MaxOneErrorPerSection !== 0;
          const secErrors    = maxOne && rawErrors    > 0 ? 1 : rawErrors;
          const secFeedbacks = maxOne && rawFeedbacks > 0 ? 1 : rawFeedbacks;
          return ecQs.length > 0 ? (
            <Space size={8}>
              <span>{sec.Name}</span>
              <Tag color="red">{secErrors} error{secErrors !== 1 ? 's' : ''}</Tag>
              <Tag color="orange">{secFeedbacks} feedback</Tag>
            </Space>
          ) : sec.Name;
        };

        if (isCompleted) {
          return (form?.Sections || []).map((sec) => {
            const visibleQs = sec.Questions.filter((q) => {
              if (q.QuestionType === 'error_count') return answers[q.ID] !== 0 || !!notes[q.ID]?.trim() || !!praised[q.ID];
              if (q.QuestionType === 'date') return !!answers[q.ID];
              return !!(answers[q.ID]);
            });
            if (visibleQs.length === 0) return null;
            return (
              <Card key={sec.ID} size="small" title={secLabel(sec)} styles={{ body: { background: '#f0f7ff' } }}>
                {renderQuestionList(visibleQs)}
              </Card>
            );
          });
        }

        // Non-completed: sections collapse by default; auto-expand any that already have an answer
        const sectionHasContent = (sec) => sec.Questions.some((q) => {
          if (q.QuestionType === 'error_count') return (answers[q.ID] !== undefined && answers[q.ID] !== 0) || !!notes[q.ID]?.trim() || !!praised[q.ID];
          return !!(answers[q.ID]);
        });
        const defaultKeys = (form?.Sections || []).filter(sectionHasContent).map((s) => s.ID);

        return (
          <Collapse
            defaultActiveKey={defaultKeys}
            style={{ marginBottom: 8 }}
            items={(form?.Sections || []).map((sec) => ({
              key: sec.ID,
              label: secLabel(sec),
              children: renderQuestionList(sec.Questions),
            }))}
          />
        );
      })()}


      <div className={printComments ? '' : 'no-print'}>
      {/* Comments */}
      <Divider orientation="left">
        <Space size={6}><CommentOutlined /><span>Comments</span></Space>
      </Divider>

      {comments.length > 0 && (
        <List
          dataSource={comments}
          renderItem={(c) => {
            const canEdit = !c.IsSystem && c.UserID === currentUser?.ID && dayjs().diff(dayjs(c.CreatedAt), 'minute') < 60;
            const canDelete = !c.IsSystem && currentUser?.Role === 'admin';
            const isEditing = editComment.id === c.ID;
            return (
              <List.Item style={{ alignItems: 'flex-start', padding: '8px 0' }}>
                <List.Item.Meta
                  avatar={<UserAvatar userId={c.UserID} name={c.UserName} size={24} />}
                  title={
                    <Space size={8}>
                      <Typography.Text strong style={{ fontSize: 13 }}>{c.UserName}</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTS(c.CreatedAt)}</Typography.Text>
                      {canEdit && !isEditing && (
                        <Button type="text" size="small" icon={<EditOutlined />}
                          onClick={() => setEditComment({ id: c.ID, text: c.Comment })} />
                      )}
                      {canDelete && !isEditing && (
                        <Popconfirm title="Delete this comment?" onConfirm={() => handleDeleteComment(c.ID)} okText="Delete" okButtonProps={{ danger: true }}>
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      )}
                    </Space>
                  }
                  description={isEditing ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Input.TextArea rows={3} value={editComment.text}
                        onChange={(e) => setEditComment(s => ({ ...s, text: e.target.value }))} autoFocus />
                      <Space>
                        <Button type="primary" size="small" onClick={handleSaveComment}>Save</Button>
                        <Button size="small" onClick={() => setEditComment({ id: null, text: '' })}>Cancel</Button>
                      </Space>
                    </Space>
                  ) : (
                    <>
                      <Typography.Text strong={!!c.IsSystem} style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}><LinkifiedText text={c.Comment} /></Typography.Text>
                      <CommentAttachments attachments={c.Attachments} />
                    </>
                  )}
                />
              </List.Item>
            );
          }}
        />
      )}

      <div className="no-print">
      {agentCanComment ? (
        <CommentBox onSubmit={handleAddComment} loading={addingComment} value={newComment} onChange={setNewComment} />
      ) : isAgent && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Comments are only available while the submission is completed and within the dispute window, or while disputed.
        </Typography.Text>
      )}
      </div>
      </div>{/* end printComments wrapper */}

      <div className={printCoaching ? '' : 'no-print'}>
      {/* Coaching section */}
      <Divider orientation="left">
        <Space size={6}>
          <LinkOutlined />
          <span>Coaching Sessions</span>
          {canManageCoaching(currentUser) && (
            <span className="no-print">
              <Button size="small" type="primary" icon={<BookOutlined />} onClick={() => setCoachModalOpen(true)}>
                New Coaching Session
              </Button>
            </span>
          )}
        </Space>
      </Divider>

      <Table
        rowKey="ID"
        dataSource={coaching}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No coaching sessions linked to this error.' }}
        style={{ marginBottom: 12 }}
        columns={[
          {
            title: 'Title', dataIndex: 'Title', key: 'Title',
            render: (v, r) => (
              <Typography.Link onClick={() => navigate(`/coaching/sessions/${r.ID}`)}>{v}</Typography.Link>
            ),
          },
          {
            title: 'Status', dataIndex: 'Status', key: 'Status', width: 100,
            render: (v) => <Tag color={SESSION_STATUS_COLOR[v] ?? 'default'}>{SESSION_STATUS_LABEL[v] ?? v}</Tag>,
          },
          { title: 'Request Type', dataIndex: 'RequestTypeName', key: 'RequestTypeName', width: 160 },
          { title: 'Coach', dataIndex: 'CoachName', key: 'CoachName', width: 160 },
          { title: 'Due Date', dataIndex: 'DueDate', key: 'DueDate', width: 110 },
        ]}
      />
      </div>{/* end printCoaching wrapper */}


      </Space>

      {/* Print options */}
      <Modal maskClosable={false}
        title="Print Options"
        open={printOptOpen}
        onCancel={() => setPrintOptOpen(false)}
        onOk={doPrint}
        okText="Print"
        okButtonProps={{ icon: <PrinterOutlined /> }}
        width={340}
        destroyOnClose
        afterClose={printAfterModalClosed}
      >
        <Space direction="vertical" style={{ padding: '8px 0' }} size={12}>
          <Checkbox checked={printComments} onChange={(e) => setPrintComments(e.target.checked)}>
            Include comments
          </Checkbox>
          <Checkbox checked={printCoaching} onChange={(e) => setPrintCoaching(e.target.checked)}>
            Include coaching sessions list
          </Checkbox>
        </Space>
      </Modal>

      {/* Completion modal — coaching prompt */}
      <Modal
        title="Submission Completed"
        open={followUpModalOpen}
        closable={false}
        maskClosable={false}
        footer={[
          <Button key="not-required" onClick={() => { setFollowUpModalOpen(false); load(); }}>
            Not Required
          </Button>,
          <Button key="coaching" type="primary" onClick={() => { setFollowUpModalOpen(false); setCoachModalOpen(true); }}>
            Create Coaching Session
          </Button>,
        ]}
        // Wide enough for the submission history table's columns; the table
        // scrolls inside the popup on a narrower screen rather than running
        // out past its edge.
        width="min(1100px, 95vw)"
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%', padding: '8px 0' }} size={16}>
          {/* Section error stats including this submission */}
          {(() => {
            const monthKey = (sub?.DateOfError || sub?.CreateDate) ? dayjs(sub.DateOfError || sub.CreateDate).format('YYYY-MM') : null;

            // Current submission's per-section error + feedback counts
            const secErrors = {};
            const secFeedbacks = {};
            (form?.Sections || []).forEach((sec) => {
              (sec.Questions || []).filter((q) => q.QuestionType === 'error_count').forEach((q) => {
                if (answers[q.ID] === 1)          secErrors[sec.Name]    = (secErrors[sec.Name]    || 0) + 1;
                if (answers[q.ID] === 'feedback')  secFeedbacks[sec.Name] = (secFeedbacks[sec.Name] || 0) + 1;
              });
            });
            // Added from the answers in hand only while the history leaves this
            // submission out. Completing it flips the status, the history is
            // fetched again and now includes it -- adding it again counted
            // its errors twice (a 1-error section showed 2).
            const historyHasThis = flatSectionRows.some((r) => String(r.SubmissionID) === String(id));
            const allCurrentSecs = historyHasThis
              ? new Set()
              : new Set([...Object.keys(secErrors), ...Object.keys(secFeedbacks)]);

            // Merged pivot (historical + this submission)
            const pivotMap = {};
            qSecPivotData.forEach((r) => { pivotMap[r.SectionName] = { ...r }; });
            if (monthKey) {
              allCurrentSecs.forEach((sec) => {
                if (!pivotMap[sec]) pivotMap[sec] = { _key: sec, SectionName: sec };
                const prev = pivotMap[sec][monthKey] || { errors: 0, feedbacks: 0 };
                pivotMap[sec][monthKey] = {
                  errors:    prev.errors    + (secErrors[sec]    || 0),
                  feedbacks: prev.feedbacks + (secFeedbacks[sec] || 0),
                };
              });
            }
            const mergedMonths = [...new Set([...qStatsMonths, ...(monthKey ? [monthKey] : [])])].sort();
            const pivotRows = Object.values(pivotMap);
            const pivotCols = [
              { title: 'Section', dataIndex: 'SectionName', key: '__sec', ellipsis: true },
              ...mergedMonths.map((mk) => ({
                title: dayjs(mk + '-01').format('MMM YYYY'), key: mk, width: 120, align: 'center',
                render: (_, r) => {
                  const cell = r[mk];
                  if (!cell || (cell.errors === 0 && cell.feedbacks === 0)) return <span style={{ color: '#d9d9d9' }}>—</span>;
                  return (
                    <div>
                      {cell.errors > 0 && <Tag color="red" style={{ margin: 0 }}>{cell.errors} {cell.errors === 1 ? 'error' : 'errors'}</Tag>}
                      {cell.feedbacks > 0 && <Tag color="orange" style={{ margin: '2px 0 0' }}>{cell.feedbacks} {cell.feedbacks === 1 ? 'feedback' : 'feedbacks'}</Tag>}
                    </div>
                  );
                },
              })),
            ];

            // Merged flat table (historical + this submission)
            const submissionDate = (sub?.DateOfError || sub?.CreateDate || '').substring(0, 10) || null;
            const flatMap = {};
            flatSectionRows.forEach((r) => { flatMap[`${r.SubmissionID}||${r.SectionName}`] = { ...r }; });
            if (submissionDate) {
              allCurrentSecs.forEach((sec) => {
                const k = `${id}||${sec}`;
                // CreateDate is filled from the submission in hand: these rows
                // are synthesised for the submission being completed, so the
                // API history does not carry them yet and Created Date would
                // otherwise render blank on its own row. Coachings stays empty
                // by design -- this modal is what asks whether to raise one.
                if (!flatMap[k]) flatMap[k] = {
                  SubmissionID: id, SectionName: sec, DateOfError: submissionDate,
                  CreateDate: (sub?.CreateDate || '').substring(0, 10) || null,
                  TotalErrors: 0, TotalFeedbacks: 0,
                };
                flatMap[k].TotalErrors    += secErrors[sec]    || 0;
                flatMap[k].TotalFeedbacks += secFeedbacks[sec] || 0;
              });
            }
            const flatRows = Object.values(flatMap).sort((a, b) =>
              a.SectionName.localeCompare(b.SectionName) || a.DateOfError.localeCompare(b.DateOfError)
            );

            if (!pivotRows.length && !flatRows.length) return null;
            const rangeLabel = qHistoryRange
              ? `${qHistoryRange.start.format('MMM D, YYYY')} — ${qHistoryRange.end.format('MMM D, YYYY')}`
              : null;
            return (
              <>
                {pivotRows.length > 0 && (
                  <>
                    <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
                      Completed errors for {sub?.AgentName}{rangeLabel ? ` ${rangeLabel}` : ''} (including this submission)
                    </Typography.Text>
                    <Table rowKey="_key" dataSource={pivotRows} columns={pivotCols} size="small"
                      pagination={false} scroll={{ x: 'max-content' }} bordered style={{ marginBottom: 12 }} />
                  </>
                )}
                {flatRows.length > 0 && (
                  <>
                    <Typography.Text style={{ display: 'block', marginBottom: 6 }}>
                      This table shows each individual completed error submission
                    </Typography.Text>
                    <Table
                    rowKey={(r) => `${r.SubmissionID}||${r.SectionName}`}
                    dataSource={flatRows}
                    size="small"
                    pagination={false}
                    bordered
                    scroll={{ x: 'max-content' }}
                    columns={[
                      { title: 'Submission ID', dataIndex: 'SubmissionID', key: 'SubmissionID', width: 120, align: 'right',
                      // window.open, not <a target="_blank">: Chrome clones sessionStorage
                      // into a script-opened window but not into one opened by an anchor.
                      // MSAL keeps the signed-in account there, so the anchor version
                      // landed on a fresh sign-in. Same approach as AgentLink on the DBAL
                      // page, which is why that one has always worked.
                      render: (v) => (
                        <Typography.Link onClick={() => window.open(`/errors/${v}`, '_blank')}>
                          {v}
                        </Typography.Link>
                      ) },
                      { title: 'Section',   dataIndex: 'SectionName', key: 'SectionName', width: 200, ellipsis: true },
                      { title: 'Date of Error', dataIndex: 'DateOfError', key: 'DateOfError', width: 120 },
                      { title: 'Created Date',  dataIndex: 'CreateDate',  key: 'CreateDate',  width: 120 },
                      {
                        // Coaching sessions raised off that submission, canceled ones
                        // excluded by the API. window.open for the same reason as the
                        // Submission ID link above.
                        title: 'Coachings', dataIndex: 'Coachings', key: 'Coachings', width: 260,
                        render: (list) => !list?.length
                          ? <Typography.Text type="secondary">-</Typography.Text>
                          : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {list.map((c) => (
                                <div key={c.ID} style={{ fontSize: 12, lineHeight: 1.4 }}>
                                  <Typography.Link onClick={() => window.open(`/coaching/sessions/${c.ID}`, '_blank')}>
                                    #{c.ID}
                                  </Typography.Link>
                                  {c.CreatedOn && <span style={{ color: '#8c8c8c' }}> · {c.CreatedOn}</span>}
                                  {c.RequestType && <span style={{ color: '#8c8c8c' }}> · {c.RequestType}</span>}
                                  {c.Linked != null && <span style={{ color: '#8c8c8c' }}> · {c.Linked ? 'Linked Sections' : 'All Sections'}</span>}
                                  <Tag
                                    color={SESSION_STATUS_COLOR[c.Status] ?? 'default'}
                                    style={{ marginLeft: 6 }}
                                  >
                                    {SESSION_STATUS_LABEL[c.Status] ?? c.Status}
                                  </Tag>
                                </div>
                              ))}
                            </div>
                          ),
                      },
                      { title: 'Errors',    dataIndex: 'TotalErrors',    key: 'TotalErrors',    width: 90,  align: 'center',
                        render: (v) => v > 0 ? <Tag color="red">{v}</Tag>    : <span style={{ color: '#d9d9d9' }}>—</span> },
                      { title: 'Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 100, align: 'center',
                        render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <span style={{ color: '#d9d9d9' }}>—</span> },
                    ]}
                  />
                  </>
                )}
              </>
            );
          })()}
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Create a coaching session for this submission, or mark it as not required.
          </Typography.Text>
        </Space>
      </Modal>

      {/* Dispute modal */}
      <Modal maskClosable={false}
        title="Dispute Error Submission"
        open={disputeOpen}
        onOk={handleDispute}
        onCancel={() => { setDisputeOpen(false); setDisputeNote(''); }}
        confirmLoading={disputing}
        okText="Submit Dispute"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Please provide a reason for disputing this submission. Your QA team will review it.
        </Typography.Paragraph>
        <Input.TextArea
          rows={4}
          value={disputeNote}
          onChange={(e) => setDisputeNote(e.target.value)}
          placeholder="Explain why you are disputing this error submission…"
          maxLength={4000}
          showCount
        />
      </Modal>

      {/* Coaching modal */}
      <CoachingCreateModal
        open={coachModalOpen}
        agentUserId={sub?.AgentUserID ?? null}
        agentName={sub?.AgentName ?? null}
        // Sessions raised from an error carry the error number in the title so
        // they are identifiable from a session list, where the linked error is
        // not otherwise visible
        initialTitle={id
          ? `Coaching Session — ${sub?.AgentName ? `${sub.AgentName} ` : ''}Error ID ${id}`
          : undefined}
        initialCampaign={sub?.Campaign_Name ?? null}
        initialCampaignId={sub?.CampaignID ?? null}
        campaignLocked={!!sub?.CampaignID}
        currentUser={currentUser}
        navigate={navigate}
        sourceType="error"
        sourceId={id ? parseInt(id) : null}
        onClose={() => { setCoachModalOpen(false); load(); }}
        onCreated={() => { setCoachModalOpen(false); load(); }}
      />

    </>
  );
}
